<template>
  <div>
    <DropdownContainer ref="dropdownContainerContactForm" :modal-size="'large'">
      <div class="contact-form">
        <div class="contact-form__content">
          <div class="contact-form__header contact-form__push-bottom">
            Kontakt {{ vendorInformation.singular }}
          </div>

          <ContactDetails
            :contactInformation="contactInformation"
            :id="id"
            :vendor-information="vendorInformation"
            :origin-source="originSource"
          />

          <div v-if="contactOptions.length > 0">
            <List
              v-if="contactOptions.length > 1"
              class="contact-form__push-bottom"
              :type="'radio'"
              :search-type="'radio-group--contact-form'"
              :items="contactOptions"
              :initial-selection="initialSelectionContactOption"
              :event-bus="proxyBus"
            ></List>

            <div class="contact-form__push-bottom">
              <p class="error my-1" v-if="userNameErrorMessage">
                <span>{{ userNameErrorMessage }}</span>
                <span class="error__attention"></span>
              </p>
              <div class="input-container">
                <input
                  class="error--input form-control"
                  title="Name"
                  type="text"
                  id="input_contact-form_name"
                  placeholder="Skriv dit navn her"
                  v-model="userName"
                />
                <label class="control-label" for="input_contact-form_name">
                  Navn
                </label>
              </div>
            </div>

            <div class="contact-form__push-bottom">
              <p class="error my-1" v-if="userEmailErrorMessage">
                <span>{{ userEmailErrorMessage }}</span>
                <span class="error__attention"></span>
              </p>
              <div class="input-container">
                <input
                  class="form-control"
                  title="Email"
                  type="text"
                  id="input_contact-form_email"
                  placeholder="Skriv din email her"
                  v-model="userEmail"
                />
                <label class="control-label" for="input_contact-form_email">
                  Email
                </label>
              </div>
            </div>

            <div class="contact-form__push-bottom">
              <p class="error my-1" v-if="userPhoneErrorMessage">
                <span>{{ userPhoneErrorMessage }}</span>
                <span class="error__attention"></span>
              </p>
              <div class="input-container">
                <input
                  class="form-control"
                  title="Phone"
                  type="text"
                  id="input_contact-form_phone"
                  placeholder="Skriv dit telefonnummer her"
                  v-model="userPhone"
                />
                <label class="control-label" for="input_contact-form_phone">
                  Telefonnummer
                </label>
              </div>
            </div>

            <div
              class="contact-form__exchance-car--container"
              v-if="selectedOption.showExchangeCar"
            >
              <div
                class="checkbox checkbox--highlight contact-form__push-bottom"
              >
                <input
                  aria-hidden="true"
                  type="checkbox"
                  v-model="hasExchangeCar"
                  id="cb3"
                />
                <label
                  for="cb3"
                  class="inline-flex items-center justify-between"
                >
                  <span class="input-visual"></span>
                  Lad min bil indgå i handlen
                </label>
              </div>

              <div
                class="contact-form__push-bottom input-container"
                v-if="hasExchangeCar"
              >
                <input
                  class="form-control"
                  title="Nummerplade på byttebil"
                  type="text"
                  id="input_contact-form_exchange-car--license-plate"
                  placeholder="Skriv nummerplade (reg. nr.) her"
                  v-model="exchangeCarRegNo"
                />
                <label
                  class="control-label"
                  for="input_contact-form_exchange-car--license-plate"
                >
                  Nummerplade
                </label>
              </div>
            </div>

            <div class="contact-form__push-bottom--light">
              <p class="error my-1" v-if="userMessageErrorMessage">
                <span>{{ userMessageErrorMessage }}</span>
                <span class="error__attention"></span>
              </p>
              <div class="input-container">
                <textarea
                  class="contact-form__description form-control"
                  title="Tekst beskrivelse"
                  type="text"
                  id="input_contact-form_text"
                  :placeholder="selectedOption.descriptionPlaceholder"
                  v-model="userMessage"
                />
                <label class="control-label" for="input_contact-form_text">
                  Din besked
                </label>
              </div>
            </div>

            <div class="contact-form__recaptcha">
              <p class="error my-1" v-if="recaptchaErrorMessage">
                <span>{{ recaptchaErrorMessage }}</span>
                <span class="error__attention"></span>
              </p>
              <div data-callback="recaptchaCallback" ref="recaptcha"></div>
            </div>

            <div class="contact-form__button-container">
              <button
                class="button button--invert"
                @click.prevent="closeForm()"
              >
                Luk
              </button>
              <button
                class="button button--highlight button--invert-disabled"
                :disabled="inProgress"
                @click.prevent="sendForm()"
              >
                Send
                <ButtonSpinner v-if="inProgress" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </DropdownContainer>
  </div>
</template>

<script>
import { app } from '@/main'
import mitt from 'mitt'
import { nextTick } from 'vue'

import List from '@search/List/List.vue'
import DropdownContainer from '@shared/DropdownContainer/DropdownContainer.vue'
import ButtonSpinner from '@components/Shared/ButtonSpinner/ButtonSpinner.vue'
import ContactDetails from './ContactDetails.vue'

import {
  LogTestDriveRequestSent,
  LogBidSent,
  LogContactSent,
  LogCriteoConfirmation,
  LogFacebookLead,
  LogCompanyContact,
} from '@/events'

import UidMixin from '@shared/uid-mixin.vue'

export default {
  mixins: [UidMixin],
  components: {
    List,
    DropdownContainer,
    ButtonSpinner,
    ContactDetails,
  },
  props: {
    userInformation: { default: null, type: Object },
    contactInformation: { default: null, type: Object },
    recaptchaSitekey: { default: '', type: String },
    contactOptions: { default: () => [], type: Array },
    id: { default: undefined, type: Number },
    vendorInformation: { default: undefined, type: Object },
    originSource: { default: '', type: String },
  },
  data() {
    return {
      uid: null,
      proxyBus: mitt(),

      selectedOptionValue: 0,
      selectedOption: this.contactOptions[0],
      initialSelectionContactOption: [],

      userName: '',
      userEmail: '',
      userPhone: '',
      userMessage: '',
      hasExchangeCar: false,
      exchangeCarRegNo: '',
      exchangeCarDescription: '',

      errorHappened: false,
      userNameErrorMessage: '',
      userEmailErrorMessage: '',
      userPhoneErrorMessage: '',
      userMessageErrorMessage: '',

      recaptchaErrorMessage: '',

      inProgress: false,

      recaptchaRendered: false,
      recaptchaIndex: undefined,
    }
  },
  methods: {
    init() {
      // Her sætter vi brugerens informationer, hvis brugeren altså er logget ind
      // Det er til felterne i formularen
      if (this.userInformation != null) {
        this.userName = this.userInformation.name
        this.userEmail = this.userInformation.email
        this.userPhone = this.userInformation.phoneNumber
      }
    },
    closeForm() {
      this.$refs.dropdownContainerContactForm.close()

      window.grecaptcha.reset(this.recaptchaIndex)
    },
    setSelectedOption(value) {
      const index = this.contactOptions.findIndex(el => el.value === value)

      if (index > -1) {
        const contactOption = this.contactOptions[index]
        contactOption.selected = true
        this.selectedOption = contactOption
        this.selectedOptionValue = index
      }
    },
    openForm(contactInformationId) {
      if (contactInformationId !== undefined) {
        this.setSelectedOption(contactInformationId)
      }

      this.initialSelectionContactOption = []
      if (contactInformationId) {
        const preSelectedContactOption = this.contactOptions.filter(item => {
          return item.value === contactInformationId
        })

        this.initialSelectionContactOption = preSelectedContactOption
      }

      this.$refs.dropdownContainerContactForm.open()

      this.$nextTick(() => {
        if (
          window.grecaptcha !== undefined &&
          !this.recaptchaRendered &&
          this.$refs.recaptcha !== undefined
        ) {
          this.recaptchaIndex = window.grecaptcha.render(this.$refs.recaptcha, {
            sitekey: this.recaptchaSitekey,
          })
          this.recaptchaRendered = true
        }
      })
    },
    clearErrorMessages() {
      this.errorHappened = false
      this.userNameErrorMessage = ''
      this.userEmailErrorMessage = ''
      this.userPhoneErrorMessage = ''
      this.userMessageErrorMessage = ''
      this.recaptchaErrorMessage = ''
      this.recaptchaRendered = false
    },
    clearInput() {
      this.init()
      this.userMessage = ''
      this.exchangeCarRegNo = ''
      this.exchangeCarDescription = ''
      this.hasExchangeCar = false
    },
    recaptchaCallback() {
      this.recaptchaErrorMessage = ''
    },
    formValidation() {
      this.clearErrorMessages()

      if (this.isNullOrEmpty(this.userName)) {
        this.userNameErrorMessage = 'Du skal udfylde dit navn'
        this.errorHappened = true
      }

      if (
        this.isNullOrEmpty(this.userEmail) &&
        this.isNullOrEmpty(this.userPhone)
      ) {
        this.userEmailErrorMessage =
          'Angiv enten email adresse eller telefonnumer'
        this.errorHappened = true
      }

      if (this.isNullOrEmpty(this.userMessage)) {
        switch (this.selectedOption.contactTypeId) {
          case 1:
            this.userMessageErrorMessage = 'Angiv hvad det drejer sig om'
            this.errorHappened = true
            break
          case 2:
            this.userMessageErrorMessage =
              'Angiv et reelt bud til ' + this.vendorInformation.singular
            this.errorHappened = true
            break
          default:
            break
        }
      }

      if (window.grecaptcha.getResponse(this.recaptchaIndex) === '') {
        this.recaptchaErrorMessage = 'Du skal bevise at du ikke er en robot'
        this.errorHappened = true
      }
    },
    sendForm() {
      this.inProgress = true
      this.formValidation()

      if (this.errorHappened) {
        this.inProgress = false
        window.grecaptcha.reset(this.recaptchaIndex)
        return
      }

      const owner = this

      let postData = {
        name: this.userName,
        email: this.userEmail,
        phone: this.userPhone,
        message: this.userMessage,
        adId: this.id,
        contactInformationId: this.selectedOption.value,
        recaptcha: window.grecaptcha.getResponse(this.recaptchaIndex),
      }

      if (this.hasExchangeCar) {
        postData.exchangeCar = this.hasExchangeCar
        postData.exchangeCarRegNo = this.exchangeCarRegNo
        postData.exchangeCarDescription = this.exchangeCarDescription
      }

      app.axios
        .post(this.selectedOption.endpoint, postData)
        .then(resp => {
          if (resp.data.errorCollection.length === 0) {
            owner.closeForm()
            this.clearInput()

            switch (this.selectedOption.contactTypeId) {
              case 1:
                LogContactSent()
                break
              case 2:
                LogBidSent()
                break
              case 3:
                LogTestDriveRequestSent()
                break
              default:
                break
            }
            nextTick(() => {
              app.toast.success(this.selectedOption.successMessage)

              if (this.selectedOption.IsCompanyContact) {
                LogCompanyContact()
              } else {
                LogCriteoConfirmation(owner.adId, owner.userEmail)
                LogFacebookLead()
              }
            })
          } else if (
            resp.data.errorCollection &&
            resp.data.errorCollection.length > 0
          ) {
            resp.data.errorCollection.forEach(value => {
              switch (value.errorItem.toLowerCase()) {
                case 'email':
                  owner.userEmailErrorMessage = value.errorMessage
                  break
                case 'phone':
                  owner.userPhoneErrorMessage = value.errorMessage
                  break
                case 'message':
                  owner.userMessageErrorMessage = value.errorMessage
                  break
                case 'recaptcha':
                  owner.recaptchaErrorMessage = value.errorMessage
                  break
                default:
                  app.toast.error(value.errorMessage)
                  break
              }
            })
          }
          this.inProgress = false
          window.grecaptcha.reset(this.recaptchaIndex)
        })
        .catch(() => {
          // an unnamed error occured in backend - reset form
          this.inProgress = false
          window.grecaptcha.reset(this.recaptchaIndex)
        })
    },
    contactFormSelectionCallback(options) {
      this.setSelectedOption(options.value)

      this.clearErrorMessages()
      this.hasExchangeCar = false
    },
    isNullOrEmpty(str) {
      if (str === null) return true
      if (typeof str === 'undefined') return true
      if (str === '') return true
      if (str.trim() === '') return true
      return false
    },
  },
  watch: {
    hasExchangeCar(newValue) {
      if (!newValue) {
        this.exchangeCarRegNo = ''
        this.exchangeCarDescription = ''
      }
    },
    userName() {
      this.userNameErrorMessage = ''
    },
    userEmail() {
      this.userEmailErrorMessage = ''
    },
    userPhone() {
      this.userPhoneErrorMessage = ''
    },
    userMessage() {
      this.userMessageErrorMessage = ''
    },
  },
  created() {
    this.proxyBus.on(
      'search-request-callback',
      this.contactFormSelectionCallback
    )

    window.recaptchaCallback = this.recaptchaCallback

    this.init()
  },
}
</script>
